import { ContentStateInterface } from '../../../../../../@types/types'
import {
  AreaCoverageOption,
  ConstructibleAfterOption,
  ConstructionPhaseOption,
  FlooringTypeOption,
  HeatingTypeOption,
  LocationClassificationTypeOption,
  SupplyTypeOption,
} from '../../../../../../@types/dropdown-types'
import { RealEstateDetails } from '../../../../../../@types/real-estate-api-types'
import { garageInformationToApi } from './garageInformationToApi'
import { cutOfLeadingZeros } from '../../../../../../services/conversion/conversionServices'
import { DE, parseDecimal } from '../../../../../../i18next/decimal'
import { rentDurationsToApi } from './rentDurationsToApi'
import { parseInteger } from '../../../../../../i18next/integer'
import { siteRecommendedUseTypesToApi } from './siteRecommendedUseTypesToApi'

export const detailsToApi = (insertionData: ContentStateInterface): RealEstateDetails => ({
  interiorQuality: insertionData.interiorQuality,
  garageInformation: garageInformationToApi(insertionData),
  flooringType: insertionData.flooringType ?? FlooringTypeOption.NO_INFORMATION,
  mainFloorSpace: parseDecimal(insertionData.mainFloorSpace, DE),
  separableSpace: parseDecimal(insertionData.separableSpace, DE),
  freeFrom: insertionData.freeFrom ? insertionData.freeFrom.toLocaleDateString(DE) : undefined,
  startRentalDate: insertionData.startRentalDate ? insertionData.startRentalDate.toLocaleDateString(DE) : undefined,
  heatingType: insertionData.constructionHeatingType ?? HeatingTypeOption.NO_INFORMATION,
  liftingPlatform: insertionData.estateFeatures?.liftingPlatform,
  plotArea: parseDecimal(insertionData.plotArea, DE),
  totalFloorSpace: parseDecimal(insertionData.totalFloorSpace, DE),
  floorLoad: parseDecimal(insertionData.floorLoad, DE),
  powerSupply: parseInteger(insertionData.powerSupply),
  loadLifting: insertionData.loadLifting,
  loadLiftingValue: parseDecimal(insertionData.loadLiftingValue, DE),
  goodsLift: insertionData.goodsLift,
  goodsLiftLoad: parseDecimal(insertionData.goodsLiftLoad, DE),
  crane: insertionData.crane,
  craneValue: parseDecimal(insertionData.craneValue, DE),
  livingSpace: parseDecimal(insertionData.livingSpace, DE),
  noRooms: parseDecimal(insertionData.room, DE),
  moveInDate: insertionData.moveInDate ? insertionData.moveInDate.toLocaleDateString(DE) : undefined,
  petsAllowed: insertionData.petsAllowed,
  noBathrooms: parseDecimal(insertionData.noBathrooms, DE),
  noBedrooms: parseDecimal(insertionData.noBedrooms, DE),
  elevator: insertionData.estateFeatures?.elevator,
  ramp: insertionData.estateFeatures?.ramp,
  listed: insertionData.estateFeatures?.listed,
  equippedKitchen: insertionData.estateFeatures?.equippedKitchen,
  handicappedAccessible: insertionData.estateFeatures?.handicappedAccessible,
  basement: insertionData.estateFeatures?.basement,
  balconyTerrace: insertionData.estateFeatures?.balconyTerrace,
  guestToilet: insertionData.estateFeatures?.guestToilet,
  sharedGarden: insertionData.estateFeatures?.sharedGarden,
  flatShareSuitable: insertionData.estateFeatures?.flatShareSuitable,
  certificateOfEligibilityNeeded: insertionData.estateFeatures?.certificateOfEligibilityNeeded,
  dishwasher: insertionData.estateFeatures?.dishwasher,
  washingMachine: insertionData.estateFeatures?.washingMachine,
  bathHasWc: insertionData.estateFeatures?.bathHasWc,
  bathHasShower: insertionData.estateFeatures?.bathHasShower,
  bathHasTub: insertionData.estateFeatures?.bathHasTub,
  internet: insertionData.estateFeatures?.internet,
  tv: insertionData.estateFeatures?.tv,
  summerResidencePractical: insertionData.estateFeatures?.summerResidencePractical,
  lodgerFlat: insertionData.estateFeatures?.lodgerFlat,
  monument: insertionData.estateFeatures?.monument,
  nonSmoker: insertionData.estateFeatures?.nonSmoker,
  terrace: insertionData.estateFeatures?.terrace,
  shortTermConstructible: insertionData.estateFeatures?.shortTermConstructible,
  buildingPermission: insertionData.estateFeatures?.buildingPermission,
  demolition: insertionData.estateFeatures?.demolition,
  floor: parseInteger(insertionData.floor),
  noFloors: cutOfLeadingZeros(insertionData.totalFloorsNumber),
  usableArea: parseDecimal(insertionData.usableArea, DE),
  widthGarage: parseDecimal(insertionData.widthGarage, DE),
  heightGarage: parseDecimal(insertionData.heightGarage, DE),
  lengthGarage: parseDecimal(insertionData.lengthGarage, DE),
  freeUntil: insertionData.freeUntil ? insertionData.freeUntil.toLocaleDateString(DE) : undefined,
  rentDurations: insertionData.rentDurations ? rentDurationsToApi(insertionData) : undefined,
  hasCanteen: insertionData.estateFeatures?.hasCanteen,
  kitchenComplete: insertionData.estateFeatures?.kitchenComplete,
  highVoltage: insertionData.estateFeatures?.highVoltage,
  lanCables: insertionData.lanCables,
  airConditioning: insertionData.airConditioning,
  minimumTermOfLease: parseInteger(insertionData.minimumTermOfLease),
  requestedGender: insertionData.requestedGender,
  flatShareSize: parseInteger(insertionData.flatShareSize),
  smokingAllowed: insertionData.smokingAllowed,
  furnishing: insertionData.furnishing,
  noRequestedFlatMates: parseInteger(insertionData.numberOfRequestedFlatMates),
  ageOfRequestedFrom: parseInteger(insertionData.ageOfRequestedFrom),
  ageOfRequestedTo: parseInteger(insertionData.ageOfRequestedTo),
  noMaleFlatMates: parseInteger(insertionData.numberOfMaleFlatMates),
  noFemaleFlatMates: parseInteger(insertionData.numberOfFemaleFlatMates),
  ageOfFlatMatesFrom: parseInteger(insertionData.ageOfFlatMatesFrom),
  ageOfFlatMatesTo: parseInteger(insertionData.ageOfFlatMatesTo),
  roomSize: parseDecimal(insertionData.roomSize, DE),
  rentSubsidy: parseDecimal(insertionData.rentSubsidy, DE),
  gfz: parseDecimal(insertionData.gfz, DE),
  grz: parseDecimal(insertionData.grz, DE),
  minRentalTime: parseInteger(insertionData.minRentalTime),
  maxRentalTime: parseInteger(insertionData.maxRentalTime),
  maxNumberOfPersons: parseInteger(insertionData.maxNumberOfPersons),
  frontWindowLength: parseDecimal(insertionData.frontWindowLength, DE),
  numberOfSeats: parseInteger(insertionData.numberOfSeats),
  numberOfBeds: parseInteger(insertionData.numberOfBeds),
  areaCoverage: insertionData.areaCoverage ?? AreaCoverageOption.NO_INFORMATION,
  constructibleAfter: insertionData.constructibleAfter ?? ConstructibleAfterOption.NO_INFORMATION,
  locationClassificationType:
    insertionData.locationClassificationType ?? LocationClassificationTypeOption.NO_INFORMATION,
  supplyType: insertionData.supplyType ?? SupplyTypeOption.NO_INFORMATION,
  utilizationTradeSite: insertionData.usageType,
  constructionPhase: insertionData.constructionPhase ?? ConstructionPhaseOption.NO_INFORMATION,
  siteRecommendedUseTypes: siteRecommendedUseTypesToApi(insertionData),
  rentalIncome: parseDecimal(insertionData.rentalIncome, DE),
  rented: insertionData.rented,
  auxiliaryArea: parseDecimal(insertionData.auxiliaryArea, DE),
  priceMultiplier: parseInteger(insertionData.priceMultiplier),
  rentalIncomeActual: parseDecimal(insertionData.rentalIncomeActual, DE),
  rentalIncomeTarget: parseDecimal(insertionData.rentalIncomeTarget, DE),
  industrialArea: parseDecimal(insertionData.industrialArea, DE),
  pricePerSQM: parseDecimal(insertionData.pricePerSQM, DE),
  serviceCharge: parseDecimal(insertionData.serviceCharge, DE),
  additionalArea: parseDecimal(insertionData.additionalArea, DE),
})
